import React, { useState, useEffect } from "react";
import { Menu, Button, Avatar, Badge, Drawer } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  TagsOutlined,
  ShopOutlined,
  ToolOutlined,
  CarOutlined,
  UploadOutlined,
  LogoutOutlined,
  BellOutlined,
  LineChartOutlined,
  EnvironmentOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.png";

/**
 * Layout responsivo:
 *  - Sider como Drawer en móviles, fijo en desktop.
 *  - Header adaptativo.
 *  - Footer responsivo.
 *  - Contenido con scroll vertical.
 */

// Variables para tamaños fijos
const FIXED_HEADER_HEIGHT = 64;
const FIXED_FOOTER_HEIGHT = 50;
const SIDER_EXPANDED_WIDTH = 200;
const SIDER_COLLAPSED_WIDTH = 70;
const MOBILE_BREAKPOINT = 768; // Punto de quiebre para móviles

const Layout = () => {
  const { role, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // En desktop, cerrar drawer y usar sidebar normal
      if (!mobile && drawerVisible) {
        setDrawerVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [drawerVisible]);

  // Función para colapsar el sider
  const toggleCollapsed = () => {
    if (isMobile) {
      setDrawerVisible(!drawerVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // Función de logout
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Cerrar drawer al cambiar de ruta en móviles
  const handleMenuClick = () => {
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // Definir ruta principal según rol
  let dashboardRoute = "/";
  if (role === "ADMIN") dashboardRoute = "/admin";
  else if (role === "CLIENT_ADMIN") dashboardRoute = "/client-dashboard";
  else if (role === "CLIENT_USER") dashboardRoute = "/client-user-dashboard";

  // Construir items de menú
  const menuItems = [];

  // Solo mostrar Distancias para ADMIN y CLIENT_ADMIN
  if (role !== "CLIENT_USER") {
    menuItems.push({
      label: (
        <Link to={dashboardRoute} onClick={handleMenuClick}>
          Distancias
        </Link>
      ),
      key: "dashboard",
      icon: <EnvironmentOutlined />,
    });
  }

  // Ejemplo extra para ADMIN
  if (role === "ADMIN") {
    menuItems.push(
      // Grupo de Administración
      {
        label: "Administración",
        key: "admin_group",
        icon: <UserOutlined />,
        children: [
          {
            label: (
              <Link to="/users" onClick={handleMenuClick}>
                Usuarios
              </Link>
            ),
            key: "users",
            icon: <UserOutlined />,
          },
          {
            label: (
              <Link to="/brands" onClick={handleMenuClick}>
                Marcas
              </Link>
            ),
            key: "brands",
            icon: <TagsOutlined />,
          },
          {
            label: (
              <Link to="/agencies" onClick={handleMenuClick}>
                Agencias
              </Link>
            ),
            key: "agencies",
            icon: <ShopOutlined />,
          },
          {
            label: (
              <Link to="/vehicles" onClick={handleMenuClick}>
                Vehículos
              </Link>
            ),
            key: "vehicles",
            icon: <CarOutlined />,
          },
        ],
      },

      // Grupo de Dashboards
      {
        label: "Dashboards",
        key: "dashboards_group",
        icon: <LineChartOutlined />,
        children: [
          {
            label: (
              <Link to="/admin" onClick={handleMenuClick}>
                Dashboard
              </Link>
            ),
            key: "admin_dashboard",
            icon: <LineChartOutlined />,
          },
          {
            label: (
              <Link to="/client-dashboard" onClick={handleMenuClick}>
                Dashboard Cliente
              </Link>
            ),
            key: "client_dashboard",
            icon: <LineChartOutlined />,
          },
          {
            label: (
              <Link to="/reports/sales/distances" onClick={handleMenuClick}>
                Dashboard Usuario
              </Link>
            ),
            key: "user_dashboard",
            icon: <LineChartOutlined />,
          },
          {
            label: (
              <Link to="/client-dashboard/map" onClick={handleMenuClick}>
                Mapa de Concentración
              </Link>
            ),
            key: "map",
            icon: <EnvironmentOutlined />,
          },
          // {
          //   label: (
          //     <Link to="/client-dashboard/traffic" onClick={handleMenuClick}>
          //       Mapa de Tráfico
          //     </Link>
          //   ),
          //   key: "traffic",
          //   icon: <LineChartOutlined />,
          // },
          {
            label: (
              <Link to="/client-dashboard/rfm" onClick={handleMenuClick}>
                Análisis RFM
              </Link>
            ),
            key: "rfm",
            icon: <LineChartOutlined />,
          },
          {
            label: (
              <Link
                to="/client-dashboard/moving-traffic"
                onClick={handleMenuClick}
              >
                Mapa de Tráfico en Vivo
              </Link>
            ),
            key: "moving_traffic",
            icon: <LineChartOutlined />,
          },
        ],
      },

      // Grupo de Ventas
      {
        label: "Ventas",
        key: "sales_group",
        icon: <LineChartOutlined />,
        children: [
          {
            label: (
              <Link to="/reports/upload" onClick={handleMenuClick}>
                Subir Reporte
              </Link>
            ),
            key: "reports_upload",
            icon: <UploadOutlined />,
          },
          {
            label: (
              <Link to="/reports/sales/upload" onClick={handleMenuClick}>
                Subir Reporte de Ventas
              </Link>
            ),
            key: "sales_report_upload",
            icon: <UploadOutlined />,
          },
          {
            label: (
              <Link to="/reports/sales/verify" onClick={handleMenuClick}>
                Verificar Ubicaciones
              </Link>
            ),
            key: "sales_location_verify",
            icon: <LineChartOutlined />,
          },
          {
            label: (
              <Link to="/reports/sales/distances" onClick={handleMenuClick}>
                Análisis de Distancias
              </Link>
            ),
            key: "sales_location_distances",
            icon: <EnvironmentOutlined />,
          },
        ],
      },

      // Grupo de Talleres
      {
        label: "Talleres",
        key: "workshops_group",
        icon: <ToolOutlined />,
        children: [
          {
            label: (
              <Link to="/talleres-no-autorizados" onClick={handleMenuClick}>
                Talleres No Autorizados
              </Link>
            ),
            key: "talleres",
            icon: <ToolOutlined />,
          },
        ],
      },

      // Grupo de Sincronización
      {
        label: "Sincronización",
        key: "sync_group",
        icon: <SyncOutlined />,
        children: [
          {
            label: (
              <Link to="/sync" onClick={handleMenuClick}>
                Panel de Sincronización
              </Link>
            ),
            key: "sync_dashboard",
            icon: <SyncOutlined />,
          },
        ],
      }
    );
  }

  // Ejemplo extra para CLIENT_ADMIN / CLIENT_USER
  if (role === "CLIENT_ADMIN") {
    menuItems.push(
      {
        label: (
          <Link to="/admin" onClick={handleMenuClick}>
            Dashboard
          </Link>
        ),
        key: "admin_dashboard",
        icon: <LineChartOutlined />,
      }
      // {
      //   label: (
      //     <Link to="/client-dashboard/traffic" onClick={handleMenuClick}>
      //       Mapa de Tráfico
      //     </Link>
      //   ),
      //   key: "traffic",
      //   icon: <LineChartOutlined />,
      // }
    );
  }

  if (role === "CLIENT_USER") {
    // Agregar opciones para CLIENT_USER
    menuItems.push(
      {
        label: (
          <Link to="/admin" onClick={handleMenuClick}>
            Dashboard
          </Link>
        ),
        key: "admin_dashboard",
        icon: <LineChartOutlined />,
      },
      {
        label: (
          <Link to="/reports/sales/distances" onClick={handleMenuClick}>
            Distancia Clientes Ayasa
          </Link>
        ),
        key: "client_distances",
        icon: <LineChartOutlined />,
      }
      // {
      //   label: (
      //     <Link to="/client-dashboard/traffic" onClick={handleMenuClick}>
      //       Mapa de Tráfico
      //     </Link>
      //   ),
      //   key: "client_traffic",
      //   icon: <LineChartOutlined />,
      // },
      ,
      {
        label: (
          <Link to="/client-dashboard/moving-traffic" onClick={handleMenuClick}>
            Vias + Concurridas
          </Link>
        ),
        key: "client_moving_traffic",
        icon: <LineChartOutlined />,
      }
    );
  }

  // Determinar el key activo según la ruta
  const getSelectedKey = (pathname) => {
    if (pathname === "/admin") return "admin_dashboard";
    if (pathname === "/client-dashboard") return "client_dashboard";
    if (pathname === "/user-dashboard") return "user_dashboard";
    if (pathname === "/client-user-dashboard") return "client_user_dashboard";
    if (pathname.startsWith("/users")) return "users";
    if (pathname.startsWith("/brands")) return "brands";
    if (pathname.startsWith("/agencies")) return "agencies";
    if (pathname.startsWith("/talleres-no-autorizados")) return "talleres";
    if (pathname.startsWith("/vehicles")) return "vehicles";
    if (pathname.startsWith("/reports/upload")) return "reports_upload";
    if (pathname.startsWith("/reports/sales/upload")) {
      if (role === "CLIENT_USER") return "client_sales_upload";
      return "sales_report_upload";
    }
    if (pathname.startsWith("/reports/sales/verify")) {
      if (role === "CLIENT_USER") return "client_locations";
      return "sales_location_verify";
    }
    if (pathname.startsWith("/reports/sales/distances")) {
      if (role === "CLIENT_USER") return "client_distances";
      return "sales_location_distances";
    }
    if (pathname.includes("/client-dashboard/map")) return "map";
    if (pathname.includes("/client-dashboard/rfm")) return "rfm";
    if (pathname.includes("/client-dashboard/traffic")) {
      if (role === "CLIENT_USER") return "client_traffic";
      return "traffic";
    }
    if (pathname.includes("/client-dashboard/moving-traffic")) {
      if (role === "CLIENT_USER") return "moving_traffic";
      return "moving_traffic";
    }
    return "dashboard";
  };

  const selectedKey = getSelectedKey(location.pathname);

  // Cálculo del ancho real del Sider según colapso
  const siderWidth = isMobile
    ? 0
    : collapsed
    ? SIDER_COLLAPSED_WIDTH
    : SIDER_EXPANDED_WIDTH;

  // Colores del modo oscuro actualizados con una paleta más moderna
  const siderBgColor = "#1a1c23";
  const headerBgColor = "#12141b";
  const contentBgColor = "#0f1117";
  const footerBgColor = "#12141b";
  const textColor = "#e3e4e8";
  const primaryColor = "#3b82f6";
  const secondaryColor = "#6366f1";
  const accentColor = "#22c55e";

  // Renderizar el menú (usado tanto en sidebar como en drawer)
  const renderMenu = () => (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      items={menuItems}
      style={{
        backgroundColor: siderBgColor,
        borderRight: 0,
      }}
    />
  );

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        background: contentBgColor,
        position: "relative",
      }}
    >
      {/* Sidebar para desktop (oculto en móviles) */}
      {!isMobile && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: siderWidth,
            height: "100vh",
            background: siderBgColor,
            zIndex: 999,
            transition: "all 0.3s ease",
            boxShadow: "2px 0 8px rgba(0,0,0,0.2)",
          }}
        >
          {/* Área del Logo */}
          <div
            style={{
              height: FIXED_HEADER_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              padding: collapsed ? 0 : "0 16px",
              background: siderBgColor,
            }}
          >
            {collapsed ? (
              <img
                src={logo}
                alt="Logo"
                style={{ height: 32, cursor: "pointer" }}
                onClick={() => setCollapsed(false)}
              />
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <img
                  src={logo}
                  alt="Logo"
                  style={{ height: 32, cursor: "pointer" }}
                  onClick={() => setCollapsed(true)}
                />
              </div>
            )}
          </div>

          {/* Menú principal */}
          {renderMenu()}
        </div>
      )}

      {/* Drawer para móviles */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={isMobile && drawerVisible}
        width={SIDER_EXPANDED_WIDTH}
        styles={{
          header: {
            padding: "16px",
            borderBottom: "1px solid #f0f0f0",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px 0",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={logo} alt="Logo" style={{ height: 32 }} />
          </div>
        </div>
        {renderMenu()}
      </Drawer>

      {/* Header adaptativo */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: siderWidth,
          right: 0,
          height: FIXED_HEADER_HEIGHT,
          background: headerBgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 998,
          padding: "0 16px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          color: textColor,
          transition: "all 0.3s ease",
        }}
      >
        {/* Botón para colapsar/expandir el sider o abrir drawer */}
        <Button
          type="text"
          style={{
            fontSize: 18,
            color: textColor,
          }}
          onClick={toggleCollapsed}
          icon={
            isMobile ? (
              <MenuUnfoldOutlined />
            ) : collapsed ? (
              <MenuUnfoldOutlined />
            ) : (
              <MenuFoldOutlined />
            )
          }
        />

        {/* Título para móviles */}
        {isMobile && (
          <div
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Tu Rastreo
          </div>
        )}

        {/* Sección de notificaciones y usuario (simplificada en móviles) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 10 : 20,
          }}
        >
          {!isMobile && (
            <Badge
              count={2}
              size="small"
              style={{ backgroundColor: accentColor }}
            >
              <BellOutlined style={{ fontSize: 20, color: textColor }} />
            </Badge>
          )}
          <Avatar
            style={{
              backgroundColor: primaryColor,
              cursor: "pointer",
            }}
            size={isMobile ? "small" : "default"}
          >
            U
          </Avatar>
          {!isMobile && (
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                backgroundColor: secondaryColor,
                border: "none",
              }}
            >
              Logout
            </Button>
          )}
          {isMobile && (
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                color: textColor,
                padding: 0,
              }}
            />
          )}
        </div>
      </div>

      {/* Footer responsivo */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: siderWidth,
          right: 0,
          height: FIXED_FOOTER_HEIGHT,
          background: footerBgColor,
          color: textColor,
          textAlign: "center",
          fontSize: isMobile ? "12px" : "14px",
          lineHeight: `${FIXED_FOOTER_HEIGHT}px`,
          zIndex: 998,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.15)",
          transition: "all 0.3s ease",
        }}
      >
        Powered by <b style={{ color: accentColor }}>Tu Rastreo</b>
      </div>

      {/* Contenido central desplazable */}
      <div
        style={{
          position: "absolute",
          top: FIXED_HEADER_HEIGHT,
          left: siderWidth,
          right: 0,
          bottom: FIXED_FOOTER_HEIGHT,
          overflowY: "auto",
          background: contentBgColor,
          color: textColor,
          padding: isMobile ? "1rem" : "1.5rem",
          transition: "all 0.3s ease",
        }}
      >
        <Outlet />
      </div>

      {/* Estilos globales adaptados para móviles */}
      <style>{`
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .ant-table {
            font-size: 12px;
          }
          .ant-card-body {
            padding: 12px !important;
          }
          .ant-typography {
            font-size: 90%;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
